import React from "react";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import CommonModal from "../../common/CommonModal";
import ProfileFormEdit from "./display/infos/ProfileFormEdit";
import ProfileFormView from "./display/infos/ProfileFormView";
import { useProfileForm } from "./hooks/useProfileForm";
import { Permission, usePermissions } from "../../../contexts/app/permissions";

type ProfileFormProps = {
  customer: ZekayCustomer | undefined;
  setProfileFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCustomerUpdated: React.Dispatch<React.SetStateAction<ZekayCustomer | undefined>>;
  editable?: boolean;
  desync?: boolean;
  onCustomerCreated?: (customer: ZekayCustomer) => void;
  closeAfterCreate?: boolean;
};

export default function ProfileForm({
  customer,
  setProfileFormOpen,
  setCustomerUpdated,
  editable = false,
  desync,
  onCustomerCreated,
  closeAfterCreate = true,
}: ProfileFormProps) {
  const effectiveEditable = Boolean(editable || desync === true);
  const showQualificationSelect = desync === false ? false : desync === true || effectiveEditable;
  const { hasPermission } = usePermissions();
  const showCustomerProducts = Boolean(
    desync &&
    effectiveEditable &&
    customer?.Id != null &&
    hasPermission(Permission.BILLING_COUNTERS_VIEW),
  );
  const showAssociateProductInCreate = Boolean(
    desync &&
    effectiveEditable &&
    (customer?.Id === undefined || customer?.Id === null) &&
    hasPermission(Permission.BILLING_COUNTERS_EDIT),
  );
  const {
    form,
    setForm,
    qualificationItems,
    saving,
    canEditProfile,
    handleClose,
    handleSave,
    title,
    showFooter,
    saveValid,
    confirmLabel,
    associateProductIds,
    setAssociateProductIds,
    hideProductsTabUntilDraftAgain,
  } = useProfileForm({
    customer,
    setProfileFormOpen,
    setCustomerUpdated,
    editable: effectiveEditable,
    onCustomerCreated,
    closeAfterCreate,
  });

  return (
    <CommonModal
      open={!!customer}
      onClose={handleClose}
      title={title}
      size="xl"
      fitContentHeight
      onConfirm={showFooter ? handleSave : undefined}
      confirmLabel={confirmLabel}
      submitDisabled={!saveValid}
      loading={saving}
    >
      {effectiveEditable && customer ? (
        <ProfileFormEdit
          form={form}
          setForm={setForm}
          customer={customer}
          canEditProfile={canEditProfile}
          setCustomerUpdated={setCustomerUpdated}
          desync={showQualificationSelect}
          qualificationItems={qualificationItems}
          showCustomerProducts={showCustomerProducts && !hideProductsTabUntilDraftAgain}
          showAssociateProductInCreate={showAssociateProductInCreate}
          associateProductIds={associateProductIds}
          setAssociateProductIds={setAssociateProductIds}
        />
      ) : customer ? (
        <ProfileFormView
          customer={customer}
          canEditProfile={canEditProfile}
          setCustomerUpdated={setCustomerUpdated}
        />
      ) : null}
    </CommonModal>
  );
}
